import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Plus, Trash2, RefreshCw, Hash, Users, Send, ArrowRight,
  Loader2, CheckCircle, XCircle, Search, Eye, Link2, AlertCircle,
  Download, UserCircle, Settings2
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function ImportDialog({ open, onOpenChange, channel, onComplete }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [limit, setLimit] = useState("all");
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);

  useEffect(() => {
    if (open) {
      loadSessions();
      setProgress(null);
      setImporting(false);
    }
  }, [open]);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const { data } = await api.get("/settings/telegram-api");
      setSessions(data.sessions || []);
      setSelectedSessions((data.sessions || []).map(s => s.id));
    } catch { }
    finally { setLoadingSessions(false); }
  };

  const toggleSession = (id) => {
    setSelectedSessions(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const startImport = async () => {
    if (selectedSessions.length === 0) {
      toast.error("En az bir operator secin");
      return;
    }
    setImporting(true);
    setProgress({ status: "Baslatiliyor...", imported: 0, total_found: 0 });
    try {
      const payload = {
        limit: limit === "all" ? null : parseInt(limit),
        session_ids: selectedSessions
      };
      const channelId = channel.id === "main" ? "main" : channel.id;
      const { data } = await api.post(`/channels/import/${channelId}`, payload);
      if (data.status === "started" || data.status === "running") {
        toast.success(data.message);
        pollStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ice aktarma basarisiz");
      setImporting(false);
    }
  };

  const pollStatus = () => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/channels/import-status");
        setProgress(data);
        if (data.status === "completed" || data.status?.startsWith("error")) {
          clearInterval(interval);
          setImporting(false);
          if (data.status === "completed") {
            toast.success("Ice aktarma tamamlandi!");
            if (onComplete) onComplete();
          }
        }
      } catch { }
    }, 3000);
  };

  const limitOptions = [
    { value: "100", label: "100" },
    { value: "500", label: "500" },
    { value: "1000", label: "1.000" },
    { value: "5000", label: "5.000" },
    { value: "10000", label: "10.000" },
    { value: "all", label: "Tumunu Aktar" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-[#27272A] max-w-lg" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2">
            <Download className="w-5 h-5 text-[#AF52DE]" />
            Aboneleri Ice Aktar
          </DialogTitle>
        </DialogHeader>

        {/* Channel Info */}
        <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A] flex items-center gap-3" data-testid="import-channel-info">
          <div className="w-10 h-10 rounded-lg bg-[#AF52DE]/10 flex items-center justify-center">
            <Hash className="w-5 h-5 text-[#AF52DE]" />
          </div>
          <div>
            <p className="text-white font-medium text-sm">{channel?.name || "Kanal"}</p>
            <p className="text-[#A1A1AA] text-xs font-mono">{channel?.telegram_channel_id || ""}</p>
          </div>
          {channel?.member_count > 0 && (
            <Badge className="ml-auto bg-[#AF52DE]/10 text-[#AF52DE] text-xs">
              {channel.member_count.toLocaleString("tr-TR")} uye
            </Badge>
          )}
        </div>

        {/* Limit Selection */}
        <div className="space-y-2" data-testid="import-limit-section">
          <Label className="text-[#A1A1AA] text-sm">Kac abone aktarilsin?</Label>
          <div className="grid grid-cols-3 gap-2">
            {limitOptions.map(opt => (
              <button
                key={opt.value}
                data-testid={`limit-${opt.value}`}
                onClick={() => setLimit(opt.value)}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors border ${
                  limit === opt.value
                    ? "bg-[#AF52DE] text-white border-[#AF52DE]"
                    : "bg-[#0A0A0A] text-[#A1A1AA] border-[#27272A] hover:border-[#AF52DE]/50 hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Session Selection */}
        <div className="space-y-2" data-testid="import-sessions-section">
          <Label className="text-[#A1A1AA] text-sm flex items-center gap-2">
            <Settings2 className="w-3.5 h-3.5" />
            Operator Hesaplari
          </Label>
          {loadingSessions ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-[#AF52DE]" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-[#FF9F0A] flex items-center gap-1.5 p-3 rounded-lg bg-[#FF9F0A]/5 border border-[#FF9F0A]/20">
              <AlertCircle className="w-3.5 h-3.5" />
              Telegram API oturumu bulunamadi. Ayarlar sayfasindan ekleyin.
            </p>
          ) : (
            <div className="space-y-1.5">
              {sessions.map(s => (
                <button
                  key={s.id}
                  data-testid={`session-select-${s.id}`}
                  onClick={() => toggleSession(s.id)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-colors text-left ${
                    selectedSessions.includes(s.id)
                      ? "bg-[#34C759]/5 border-[#34C759]/30"
                      : "bg-[#0A0A0A] border-[#27272A] hover:border-[#A1A1AA]/30"
                  }`}
                >
                  <Checkbox
                    checked={selectedSessions.includes(s.id)}
                    onCheckedChange={() => toggleSession(s.id)}
                    className="border-[#A1A1AA]"
                  />
                  <div className="w-7 h-7 rounded-full bg-[#34C759]/10 flex items-center justify-center shrink-0">
                    <UserCircle className="w-4 h-4 text-[#34C759]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">{s.label || "Oturum"}</p>
                    <p className="text-[10px] text-[#A1A1AA] font-mono">{s.phone}</p>
                  </div>
                  {selectedSessions.includes(s.id) && (
                    <Badge className="bg-[#34C759]/10 text-[#34C759] text-[10px] shrink-0">Secili</Badge>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Progress */}
        {progress && (
          <div className="p-3 rounded-lg border border-[#27272A] bg-[#0A0A0A] space-y-2" data-testid="import-progress">
            {progress.status === "completed" ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#34C759]" />
                  <span className="text-sm text-white">{progress.message}</span>
                </div>
                {progress.errors?.length > 0 && (
                  <div className="text-xs text-[#FF9F0A] mt-1">
                    Uyarilar: {progress.errors.join("; ")}
                  </div>
                )}
              </div>
            ) : progress.status?.startsWith("error") ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-[#FF3B30]" />
                  <span className="text-sm text-[#FF3B30]">{progress.status.replace("error: ", "")}</span>
                </div>
                {progress.errors?.length > 0 && (
                  <div className="text-xs text-[#A1A1AA] space-y-0.5">
                    {progress.errors.map((e, i) => <p key={i}>{e}</p>)}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-[#AF52DE] animate-spin" />
                  <span className="text-sm text-white">{progress.status || "Baslatiliyor..."}</span>
                </div>
                <div className="text-xs text-[#A1A1AA]">
                  {progress.total_found || 0} bulundu | {progress.imported || 0} yeni | {progress.updated || 0} guncellendi
                </div>
              </>
            )}
          </div>
        )}

        {/* Action Button */}
        {(!progress || (progress.status !== "completed" && !progress.status?.startsWith("error"))) && (
          <Button
            data-testid="start-import-btn"
            onClick={startImport}
            disabled={importing || selectedSessions.length === 0}
            className="w-full bg-[#AF52DE] hover:brightness-110 text-white h-10"
          >
            {importing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ice Aktariliyor...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" />Ice Aktarmaya Basla</>
            )}
          </Button>
        )}

        {/* Error: Retry button */}
        {progress?.status?.startsWith("error") && (
          <Button
            onClick={() => { setProgress(null); setImporting(false); }}
            className="w-full bg-[#FF9F0A] hover:brightness-110 text-white h-10"
          >
            Tekrar Dene
          </Button>
        )}

        {progress?.status === "completed" && (
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full bg-[#34C759] hover:brightness-110 text-white h-10"
          >
            <CheckCircle className="w-4 h-4 mr-2" />Tamam
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, onOpenChange, channels, sourceChannel, onComplete }) {
  const [targetId, setTargetId] = useState("");
  const [members, setMembers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [transferProgress, setTransferProgress] = useState(null);

  useEffect(() => {
    if (open && sourceChannel) {
      loadMembers();
      setTargetId("");
      setSelectedIds([]);
      setSelectAll(true);
      setMessage("");
      setResult(null);
      setSearchTerm("");
    }
  }, [open, sourceChannel]);

  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const url = sourceChannel.id === "main" ? "/channels/main/members" : `/channels/${sourceChannel.id}/members`;
      const { data } = await api.get(url);
      setMembers(data.users);
      setSelectedIds(data.users.map(u => u.id));
    } catch (err) { toast.error("Kullanicilar yuklenemedi"); }
    finally { setLoadingMembers(false); }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(filteredMembers.map(u => u.id));
      setSelectAll(true);
    }
  };

  const toggleUser = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const startTransfer = async () => {
    if (!targetId) { toast.error("Hedef kanal secin"); return; }
    if (selectedIds.length === 0) { toast.error("Kullanici secin"); return; }
    setSending(true);
    setResult(null);
    setTransferProgress({ status: "Baslatiliyor...", sent: 0, failed: 0, total: selectedIds.length });
    try {
      const payload = {
        source_type: sourceChannel.id === "main" ? "main" : sourceChannel.id,
        target_channel_id: targetId,
        user_ids: selectAll && selectedIds.length === members.length ? null : selectedIds,
        message: message || null,
        limit: selectAll && selectedIds.length === members.length ? selectedIds.length : null
      };
      const { data } = await api.post("/channels/transfer", payload);
      if (data.status === "started" || data.status === "running") {
        toast.success(data.message);
        pollTransferStatus();
      } else {
        setResult(data);
        setTransferProgress(null);
        toast.success(data.message);
        if (onComplete) onComplete();
        setSending(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transfer basarisiz");
      setSending(false);
      setTransferProgress(null);
    }
  };

  const pollTransferStatus = () => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/channels/transfer-status");
        setTransferProgress(data);
        if (data.status === "completed" || data.status?.startsWith("error")) {
          clearInterval(interval);
          setSending(false);
          setResult(data);
          if (data.status === "completed") {
            toast.success("Transfer tamamlandi!");
            if (onComplete) onComplete();
          }
        }
      } catch { }
    }, 3000);
  };

  const filteredMembers = members.filter(u => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (u.username || "").toLowerCase().includes(s) ||
      (u.first_name || "").toLowerCase().includes(s) ||
      (u.telegram_id || "").includes(s);
  });

  const availableTargets = channels.filter(ch => ch.id !== sourceChannel?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-[#27272A] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-[#24A1DE]" />
            Kullanici Transferi
          </DialogTitle>
        </DialogHeader>

        {/* Transfer Direction */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A]" data-testid="transfer-direction">
          <div className="flex-1 text-center">
            <p className="text-xs text-[#A1A1AA] mb-1">Kaynak</p>
            <Badge variant="outline" className="border-[#24A1DE] text-[#24A1DE]">
              <Hash className="w-3 h-3 mr-1" />{sourceChannel?.name || "Ana Kanal"}
            </Badge>
          </div>
          <ArrowRight className="w-5 h-5 text-[#34C759] shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-[#A1A1AA] mb-1 text-center">Hedef</p>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger data-testid="target-channel-select" className="bg-[#2C2C2E] border-transparent text-white h-8 text-sm">
                <SelectValue placeholder="Kanal sec..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                {availableTargets.map(ch => (
                  <SelectItem key={ch.id} value={ch.id}>
                    <span className="flex items-center gap-2"><Hash className="w-3 h-3" />{ch.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Custom Message */}
        <div className="space-y-1">
          <Label className="text-[#A1A1AA] text-xs">Davet Mesaji (opsiyonel)</Label>
          <Textarea
            data-testid="transfer-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="bg-[#2C2C2E] border-transparent text-white min-h-[60px] text-sm resize-none"
            placeholder="Ozel davet mesaji yazin..."
          />
        </div>

        {/* Member Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <p className="text-sm text-white font-medium">Kullanicilar</p>
              <Badge className="bg-[#24A1DE]/10 text-[#24A1DE] text-xs">{selectedIds.length}/{members.length} secili</Badge>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <Input
                  data-testid="transfer-search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-[#2C2C2E] border-transparent text-white h-7 text-xs pl-7 w-40"
                  placeholder="Ara..."
                />
              </div>
              <Button size="sm" variant="ghost" className="text-[#24A1DE] text-xs h-7" onClick={toggleSelectAll} data-testid="select-all-btn">
                {selectAll ? "Hicbirini Sec" : "Tumunu Sec"}
              </Button>
            </div>
          </div>

          {loadingMembers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#24A1DE]" />
            </div>
          ) : (
            <div className="border border-[#27272A] rounded-lg max-h-[280px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#27272A] hover:bg-transparent">
                    <TableHead className="w-10 text-[#A1A1AA] text-xs"></TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs">Kullanici</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs">Telegram ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.slice(0, 200).map((user) => (
                    <TableRow key={user.id} className="border-[#27272A] hover:bg-[#2C2C2E]/50 h-9" data-testid={`transfer-user-${user.id}`}>
                      <TableCell className="py-1.5">
                        <Checkbox
                          checked={selectedIds.includes(user.id)}
                          onCheckedChange={() => toggleUser(user.id)}
                          className="border-[#A1A1AA]"
                        />
                      </TableCell>
                      <TableCell className="py-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#24A1DE]/10 flex items-center justify-center text-[#24A1DE] text-[10px] font-bold shrink-0">
                            {(user.first_name || user.username || "?")[0]?.toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-xs truncate">{user.first_name || "Anonim"}</p>
                            {user.username && <p className="text-[#24A1DE] text-[10px] font-mono">@{user.username}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#A1A1AA] text-xs font-mono py-1.5">{user.telegram_id || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredMembers.length > 200 && (
                <p className="text-xs text-[#A1A1AA] text-center py-2">+{filteredMembers.length - 200} daha...</p>
              )}
            </div>
          )}
        </div>

        {/* Transfer Progress */}
        {transferProgress && !result && (
          <div className="p-3 rounded-lg border border-[#27272A] bg-[#0A0A0A] space-y-2" data-testid="transfer-progress">
            {transferProgress.status === "completed" ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#34C759]" />
                <span className="text-sm text-white">{transferProgress.message}</span>
              </div>
            ) : transferProgress.status?.startsWith("error") ? (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-sm text-[#FF3B30]">{transferProgress.status.replace("error: ", "")}</span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-[#34C759] animate-spin" />
                  <span className="text-sm text-white">{transferProgress.status || "Baslatiliyor..."}</span>
                </div>
                <div className="flex gap-3 text-xs text-[#A1A1AA]">
                  <span className="text-[#34C759]">{transferProgress.sent || 0} eklendi</span>
                  <span>{transferProgress.already_member || 0} zaten uye</span>
                  <span className="text-[#FF3B30]">{transferProgress.failed || 0} basarisiz</span>
                  <span>/ {transferProgress.total || 0} toplam</span>
                </div>
                {transferProgress.total > 0 && (
                  <div className="w-full bg-[#27272A] rounded-full h-1.5">
                    <div className="bg-[#34C759] h-1.5 rounded-full transition-all" style={{
                      width: `${Math.round(((transferProgress.sent || 0) + (transferProgress.already_member || 0) + (transferProgress.failed || 0)) / transferProgress.total * 100)}%`
                    }} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className={`p-3 rounded-lg ${result.status === 'completed' || result.sent > 0 ? 'bg-[#34C759]/5 border border-[#34C759]/20' : 'bg-[#FF3B30]/5 border border-[#FF3B30]/20'}`} data-testid="transfer-result">
            <div className="flex items-center gap-2 mb-2">
              {result.sent > 0 || result.status === 'completed' ? (
                <CheckCircle className="w-4 h-4 text-[#34C759]" />
              ) : (
                <XCircle className="w-4 h-4 text-[#FF3B30]" />
              )}
              <span className="text-sm text-white font-medium">{result.message}</span>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="text-[#34C759]">{result.sent || 0} eklendi</span>
              {(result.already_member || 0) > 0 && <span className="text-[#A1A1AA]">{result.already_member} zaten uye</span>}
              {(result.failed || 0) > 0 && <span className="text-[#FF3B30]">{result.failed} basarisiz</span>}
            </div>
          </div>
        )}

        {/* Transfer Button */}
        {!result && (
          <Button
            data-testid="start-transfer-btn"
            onClick={startTransfer}
            disabled={sending || !targetId || selectedIds.length === 0}
            className="w-full bg-[#34C759] hover:brightness-110 text-white h-10"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transfer ediliyor...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" />{selectedIds.length} Kullaniciyi Transfer Et</>
            )}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ChannelsPage() {
  const [channels, setChannels] = useState([]);
  const [mainChannel, setMainChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", telegram_channel_id: "", description: "", is_private: true });
  const [syncing, setSyncing] = useState({});
  const [transferSource, setTransferSource] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [importChannel, setImportChannel] = useState(null);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [chRes, configRes] = await Promise.all([
        api.get("/channels"),
        api.get("/settings/telegram")
      ]);
      setChannels(chRes.data.channels);

      if (configRes.data.is_configured && configRes.data.main_channel_id) {
        try {
          const countRes = await api.get("/telegram/member-count");
          const mainMembersRes = await api.get("/channels/main/members");
          setMainChannel({
            id: "main",
            name: configRes.data.channel_title || "Ana Kanal",
            telegram_channel_id: configRes.data.main_channel_id,
            member_count: countRes.data.count || 0,
            db_users: mainMembersRes.data.total || 0
          });
        } catch { setMainChannel(null); }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const addChannel = async (e) => {
    e.preventDefault();
    try {
      await api.post("/channels", form);
      toast.success("Kanal eklendi");
      setShowAdd(false);
      setForm({ name: "", telegram_channel_id: "", description: "", is_private: true });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Ekleme basarisiz"); }
  };

  const deleteChannel = async (id) => {
    try {
      await api.delete(`/channels/${id}`);
      toast.success("Kanal silindi");
      loadData();
    } catch (err) { toast.error("Silme basarisiz"); }
  };

  const syncChannel = async (id) => {
    setSyncing(p => ({ ...p, [id]: true }));
    try {
      await api.post(`/channels/${id}/sync`);
      toast.success("Senkronize edildi");
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Senkronizasyon basarisiz"); }
    finally { setSyncing(p => ({ ...p, [id]: false })); }
  };

  const openTransfer = (channel) => {
    setTransferSource(channel);
    setShowTransfer(true);
  };

  const openImport = (channel) => {
    setImportChannel(channel);
    setShowImport(true);
  };

  const allChannels = mainChannel ? [mainChannel, ...channels] : channels;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-[#24A1DE]" />
      </div>
    );
  }

  return (
    <div data-testid="channels-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Kanallar</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">Kanallarinizi yonetin ve kullanicilari kanallar arasi transfer edin</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="add-channel-btn" className="bg-[#24A1DE] hover:brightness-110 text-white">
              <Plus className="w-4 h-4 mr-2" />Kanal Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Yeni Kanal</DialogTitle>
            </DialogHeader>
            <form onSubmit={addChannel} className="space-y-4">
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Kanal Adi</Label>
                <Input data-testid="channel-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#2C2C2E] border-transparent text-white" placeholder="CRATOS YENI YIL ETKINLIGI" required />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Telegram Kanal ID</Label>
                <Input data-testid="channel-tg-id" value={form.telegram_channel_id} onChange={(e) => setForm({ ...form, telegram_channel_id: e.target.value })} className="bg-[#2C2C2E] border-transparent text-white font-mono" placeholder="-1001234567890" required />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Aciklama (opsiyonel)</Label>
                <Input data-testid="channel-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#2C2C2E] border-transparent text-white" />
              </div>
              <Button data-testid="submit-channel" type="submit" className="w-full bg-[#24A1DE] hover:brightness-110">Kanal Ekle</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* No channels */}
      {allChannels.length === 0 && (
        <Card className="bg-[#121212] border-[#24A1DE]/30">
          <CardContent className="p-8 text-center">
            <Hash className="w-10 h-10 text-[#24A1DE] mx-auto mb-3" />
            <p className="text-white font-medium mb-2">Henuz kanal yok</p>
            <p className="text-[#A1A1AA] text-sm mb-4">Once Ayarlar'dan bot'unuzu baglayip ana kanalinizi ekleyin.</p>
          </CardContent>
        </Card>
      )}

      {/* Channel Grid */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="channel-grid">
        {/* Main Channel Card */}
        {mainChannel && (
          <Card className="bg-[#121212] border-[#24A1DE]/30 relative overflow-hidden" data-testid="main-channel-card">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24A1DE]" />
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-[#24A1DE]/10 flex items-center justify-center">
                    <Hash className="w-5 h-5 text-[#24A1DE]" />
                  </div>
                  <div>
                    <CardTitle className="text-white text-base">{mainChannel.name}</CardTitle>
                    <p className="text-xs text-[#A1A1AA] font-mono">{mainChannel.telegram_channel_id}</p>
                  </div>
                </div>
                <Badge className="bg-[#24A1DE]/10 text-[#24A1DE] text-xs">Ana</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded bg-[#0A0A0A] border border-[#27272A]">
                  <p className="text-[10px] text-[#A1A1AA] uppercase">Toplam Uye</p>
                  <p className="text-lg font-bold text-white font-heading">{mainChannel.member_count}</p>
                </div>
                <div className="p-2 rounded bg-[#0A0A0A] border border-[#27272A]">
                  <p className="text-[10px] text-[#A1A1AA] uppercase">Panelde</p>
                  <p className="text-lg font-bold text-[#34C759] font-heading">{mainChannel.db_users}</p>
                </div>
              </div>
              <Button
                data-testid="transfer-from-main"
                onClick={() => openTransfer(mainChannel)}
                className="w-full bg-[#34C759] hover:brightness-110 text-white h-9"
              >
                <ArrowRight className="w-4 h-4 mr-2" />Kullanicilari Transfer Et
              </Button>
              <Button
                data-testid="import-from-main"
                onClick={() => openImport(mainChannel)}
                variant="outline"
                className="w-full border-[#AF52DE]/30 text-[#AF52DE] hover:bg-[#AF52DE]/10 h-9"
              >
                <Download className="w-4 h-4 mr-2" />Aboneleri Ice Aktar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Side Channel Cards */}
        {channels.map(ch => (
          <Card key={ch.id} className="bg-[#121212] border-[#27272A] relative overflow-hidden" data-testid={`channel-card-${ch.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-[#AF52DE]/10 flex items-center justify-center">
                    <Hash className="w-5 h-5 text-[#AF52DE]" />
                  </div>
                  <div>
                    <CardTitle className="text-white text-base">{ch.name}</CardTitle>
                    <p className="text-xs text-[#A1A1AA] font-mono">{ch.telegram_channel_id}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => syncChannel(ch.id)} disabled={syncing[ch.id]} className="h-7 w-7 p-0 text-[#A1A1AA] hover:text-white" data-testid={`sync-${ch.id}`}>
                    <RefreshCw className={`w-3.5 h-3.5 ${syncing[ch.id] ? "animate-spin" : ""}`} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteChannel(ch.id)} className="h-7 w-7 p-0 text-[#FF3B30] hover:text-[#FF3B30]" data-testid={`delete-channel-${ch.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded bg-[#0A0A0A] border border-[#27272A]">
                  <p className="text-[10px] text-[#A1A1AA] uppercase">Uye</p>
                  <p className="text-lg font-bold text-white font-heading">{ch.member_count || 0}</p>
                </div>
                <div className="p-2 rounded bg-[#0A0A0A] border border-[#27272A]">
                  <p className="text-[10px] text-[#A1A1AA] uppercase">Senkron</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">{ch.synced_at ? new Date(ch.synced_at).toLocaleDateString("tr-TR") : "-"}</p>
                </div>
              </div>
              {ch.invite_link && (
                <div className="flex items-center gap-2 p-2 rounded bg-[#24A1DE]/5 border border-[#24A1DE]/20">
                  <Link2 className="w-3 h-3 text-[#24A1DE] shrink-0" />
                  <p className="text-[10px] text-[#24A1DE] font-mono truncate">{ch.invite_link}</p>
                </div>
              )}
              <Button
                data-testid={`transfer-from-${ch.id}`}
                onClick={() => openTransfer(ch)}
                className="w-full bg-[#34C759] hover:brightness-110 text-white h-9"
              >
                <ArrowRight className="w-4 h-4 mr-2" />Kullanicilari Transfer Et
              </Button>
              <Button
                data-testid={`import-from-${ch.id}`}
                onClick={() => openImport(ch)}
                variant="outline"
                className="w-full border-[#AF52DE]/30 text-[#AF52DE] hover:bg-[#AF52DE]/10 h-9"
              >
                <Download className="w-4 h-4 mr-2" />Aboneleri Ice Aktar
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Transfer Dialog */}
      <TransferDialog
        open={showTransfer}
        onOpenChange={setShowTransfer}
        channels={channels}
        sourceChannel={transferSource}
        onComplete={loadData}
      />

      {/* Import Dialog */}
      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        channel={importChannel}
        onComplete={loadData}
      />
    </div>
  );
}
